// ───────────────────────────────────────────────────────────────
// src/hooks/useStomp.js
// React Hook으로 STOMP 연결과 구독을 관리하는 모듈

import { useEffect, useRef, useState } from 'react';
import { createStompClient } from '../lib/chat/stompClient';

/**
 * useStomp Hook
 * @param {{ brokerURL: string, token?: string, roomId: string, usrUuid?: string, onMessage: (msg: any) => void, onMentionNotification?: (notification: any) => void, onConnectionChange?: (status: string, meta?: any) => void }} params
 * @returns {Client|null}
 */
export default function useStomp({ brokerURL, token, roomId, usrUuid, onMessage, onMentionNotification, onConnectionChange }) {
  const clientRef = useRef(null);
  const [clientState, setClientState] = useState(null);
  const onMessageRef = useRef(onMessage);
  const onMentionRef = useRef(onMentionNotification);
  const onConnectionChangeRef = useRef(onConnectionChange);

  // 콜백이 바뀌어도 ref만 업데이트 (reconnect 방지)
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onMentionRef.current = onMentionNotification;
  }, [onMentionNotification]);

  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange;
  }, [onConnectionChange]);

  useEffect(() => {
    if (!roomId) {
      clientRef.current = null;
      setClientState(null);
      return undefined;
    }

    let active = true;

    const client = createStompClient({
      brokerURL,
      token,
      onConnect: () => {
        if (!active) return;
        onConnectionChangeRef.current?.('connected', { roomId });

        // 지정된 채널 구독
        client.subscribe(`/topic/chat/${roomId}`, (message) => {
          onMessageRef.current(JSON.parse(message.body));
        });

        // 멘션 알림 구독 (개인 토픽)
        if (usrUuid) {
          client.subscribe(`/topic/mention/${usrUuid}`, (message) => {
            try {
              const notification = JSON.parse(message.body);
              console.log('🔔 멘션 알림 수신:', notification);
              if (onMentionRef.current) {
                onMentionRef.current(notification);
              }
            } catch (e) {
              console.error('멘션 알림 파싱 실패:', e);
            }
          });
          console.log('🔔 멘션 알림 구독 완료:', usrUuid);
        }
      },
      onDisconnect: () => {
        if (active) {
          onConnectionChangeRef.current?.('disconnected', { roomId });
        }
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        if (active) {
          onConnectionChangeRef.current?.('error', { roomId, frame });
        }
      },
      onWebSocketError: (err) => {
        console.error('WebSocket error:', err);
        if (active) {
          onConnectionChangeRef.current?.('error', { roomId, error: err });
        }
      },
      onWebSocketClose: (event) => {
        if (active) {
          onConnectionChangeRef.current?.('disconnected', { roomId, event });
        }
      },
    });

    clientRef.current = client;
    setClientState(client);
    onConnectionChangeRef.current?.('connecting', { roomId });
    client.activate();

    return () => {
      active = false;
      clientRef.current = null;
      setClientState(null);
      client.deactivate();
    };
  }, [brokerURL, token, roomId, usrUuid]); // onMessage, onMentionNotification 제거!

  return clientState;
}
